import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
} from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import MediaPlaceholder from './MediaPlaceholder';
import { C } from '../theme';
import PeopleIcon from '@mui/icons-material/People';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LockIcon from '@mui/icons-material/Lock';

interface BusinessAd {
  id: number;
  title: string;
  description: string | null; // Can be null for limited access
  price: number;
  business_type: string;
  country_id: string;
  city_id: string;
  annual_revenue?: number | null;
  employee_count?: number | null;
  established_year?: number | null;
  author_name: string;
  thumbnail?: string;
  created_at: string;
}

interface BusinessAdCardProps {
  businessAd: BusinessAd;
}

const businessTypeLabels: Record<string, string> = {
  'restaurant_hospitality': 'Restoran i ugostiteljstvo',
  'retail_services': 'Trgovina i usluge',
  'manufacturing': 'Proizvodnja',
  'technology_it': 'Tehnologija i IT',
  'healthcare': 'Zdravstvo',
  'education': 'Obrazovanje',
  'transport_logistics': 'Transport i logistika',
  'real_estate': 'Nekretnine',
  'finance_banking': 'Finansije i bankarstvo',
  'other': 'Ostalo',
};

const BusinessAdCard: React.FC<BusinessAdCardProps> = ({ businessAd }) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const hasImage = Boolean(businessAd.thumbnail) && !imgError;

  const formatPrice = (price: number) => {
    if (price >= 1000000) {
      return `€${(price / 1000000).toFixed(1)}M`;
    } else if (price >= 1000) {
      return `€${(price / 1000).toFixed(0)}K`;
    }
    return `€${price.toLocaleString()}`;
  };

  const formatRevenue = (revenue?: number) => {
    if (!revenue) return 'N/A';
    if (revenue >= 1000000) {
      return `€${(revenue / 1000000).toFixed(1)}M`;
    } else if (revenue >= 1000) {
      return `€${(revenue / 1000).toFixed(0)}K`;
    }
    return `€${revenue.toLocaleString()}`;
  };

  const getBusinessTypeLabel = (type: string) => {
    return businessTypeLabels[type] || type;
  };

  const handleClick = () => {
    navigate(`/business/${businessAd.id}`);
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
        }
      }}
      onClick={handleClick}
    >
      {hasImage ? (
        <CardMedia
          component="img"
          height="200"
          image={`/uploads/thumbnails/${businessAd.thumbnail}`}
          alt={businessAd.title}
          onError={() => setImgError(true)}
          sx={{ objectFit: 'cover' }}
        />
      ) : (
        <MediaPlaceholder height={200} icon={<StorefrontOutlinedIcon sx={{ fontSize: 48, color: 'rgba(243,192,21,0.45)' }} />} label="Bez fotografije" />
      )}
      
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Chip
            label={getBusinessTypeLabel(businessAd.business_type)}
            size="small"
            sx={{
              mb: 1,
              backgroundColor: C.goldSoft,
              color: '#F6CB4B',
              border: '1px solid rgba(243,192,21,0.45)',
              fontWeight: 700,
            }}
          />
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
            {businessAd.title}
          </Typography>
          {businessAd.description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
              {businessAd.description.length > 100 
                ? `${businessAd.description.substring(0, 100)}...` 
                : businessAd.description
              }
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Opis je dostupan samo investitorima
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {formatPrice(businessAd.price)}
            </Typography>
          </Box>

          <Grid container spacing={1} sx={{ mb: 2 }}>
            {businessAd.annual_revenue && (
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TrendingUpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {formatRevenue(businessAd.annual_revenue)}
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {businessAd.employee_count && (
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {businessAd.employee_count} zaposlenih
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {businessAd.established_year && (
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {businessAd.established_year}
                  </Typography>
                </Box>
              </Grid>
            )}
            
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {businessAd.author_name}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Button
            variant="outlined"
            fullWidth
            size="small"
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.main',
                color: 'white',
              }
            }}
          >
            Pogledaj detalje
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BusinessAdCard;
